import * as React from 'react'
import styled, { css, CSSObject } from 'styled-components'
import { CellExtended } from './Cell'
import { TableColumn } from './types'
import { getProperty, getConditionalStyle } from './util'

interface CellStyleProps {
  $renderAsCell: boolean | undefined
  $wrapCell: boolean | undefined
  $allowOverflow: boolean | undefined
  $cellStyle: CSSObject | undefined
  $isDragging: boolean
}

const overflowCSS = css<CellStyleProps>`
  div:first-child {
    white-space: ${({ $wrapCell }) => ($wrapCell ? 'normal' : 'nowrap')};
    overflow: ${({ $allowOverflow }) =>
      $allowOverflow ? 'visible' : 'visible'};
    text-overflow: ellipsis;
  }
`

const CellStyle = styled(CellExtended).attrs((props) => ({
  style: props.style,
}))<CellStyleProps>`
  ${({ $renderAsCell }) => !$renderAsCell && overflowCSS};
  ${({ theme, $isDragging }) => $isDragging && theme.cells.draggingStyle};
  ${({ $cellStyle }) => $cellStyle};
`

interface CellProps<T> {
  id: string
  dataTag: string | null
  column: TableColumn<T>
  row: T
  rowIndex: number
  isDragging: boolean
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnter: (e: React.DragEvent<HTMLDivElement>) => void
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void
}

function Cell<T>({
  id,
  column,
  row,
  rowIndex,
  dataTag,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDragEnter,
  onDragLeave,
}: CellProps<T>): JSX.Element {
  const { conditionalStyle, classNames } = getConditionalStyle(
    row,
    column.conditionalCellStyles,
    ['rdt_TableCell'],
  )

  const extractTextFromNode = (node: React.ReactNode): string => {
    if (node == null) return ''
    if (typeof node === 'string' || typeof node === 'number') {
      return String(node)
    }
    if (Array.isArray(node)) {
      return node.map(extractTextFromNode).join(' ').trim()
    }
    if (React.isValidElement(node)) {
      return extractTextFromNode(
        (node.props as { children?: React.ReactNode }).children,
      )
    }
    return ''
  }

  const columnName: string = (() => {
    if (column.name) {
      const extracted = extractTextFromNode(column.name as React.ReactNode)
      if (extracted) return extracted
    }
    const mobileLabel = (column as TableColumn<T> & { mobileLabel?: string })
      .mobileLabel
    if (typeof mobileLabel === 'string' && mobileLabel.trim()) {
      return mobileLabel
    }
    return ''
  })()

  return (
    <CellStyle
      id={id}
      data-column-id={column.id}
      data-column-name={columnName}
      role="cell"
      className={classNames}
      data-tag={dataTag}
      $cellStyle={column.style}
      $renderAsCell={!!column.cell}
      $allowOverflow={column.allowOverflow}
      button={column.button}
      center={column.center}
      compact={column.compact}
      grow={column.grow}
      hide={column.hide}
      maxWidth={column.maxWidth}
      minWidth={column.minWidth}
      right={column.right}
      width={column.width}
      $wrapCell={column.wrap}
      style={conditionalStyle as React.CSSProperties}
      $isDragging={isDragging}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
    >
      <div className="rdt_TableCell__value" data-tag={dataTag}>
        {!column.cell
          ? getProperty(row, column.selector, column.format, rowIndex)
          : column.cell(row, rowIndex, column, id)}
      </div>
    </CellStyle>
  )
}

export default React.memo(Cell) as typeof Cell
